<template>
  <section props="props">
    <div v-if="showUploadFile" v-can="uploadCapability">
      <label>Upload File</label>
      <o-field>
        <o-upload v-model="dropFiles" multiple drag-drop>
          <section class="ex-center">
            <p>
              <o-icon icon="upload" size="medium" />
            </p>
            <p>Drop your files here or click to upload</p>
          </section>
        </o-upload>
      </o-field>
      <div class="tags">
        <ul>
          <li v-for="(file, index) in dropFiles" :key="index">
            <o-button style="padding: 0px" @click="clickDeleteFile(index)">
              <o-icon icon="delete-outline" size="medium"></o-icon>
            </o-button>
            <o-button
              v-if="!isFileUploaded(file['name'])"
              style="padding: 0px"
              @click="clickImportFile(file)"
            >
              <o-icon icon="content-save-outline" size="medium"></o-icon>
            </o-button>
            <!-- @ts-ignore -->
            <p :class="isFileUploaded(file['name']) ? 'has-background-danger' : ''">
              <b>{{ file["name"] }}</b>
            </p>
          </li>
        </ul>
      </div>
    </div>

    <div v-if="showExportFile && dropFiles.length == 0">
      <label>Download File</label>

      <div class="tags" id="download">
        <o-button data-cy="export-file-btn" style="padding-left: 6px; padding-top: 0px" @click="clickExportFile()">
          <o-icon icon="content-save-outline" size="medium" color="is-primary"></o-icon>
        </o-button>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { onMounted, ref, computed } from "vue";
import dayjs from "dayjs";

// @ts-ignore
import Papa from "papaparse";
import { useRoute } from "vue-router";
import { useAppStore } from "@/stores/app";

import { useTransactStore } from "@/stores/transact";
import { useEmployeeStore } from "@/stores/employee";
import { useDepartmentStore } from "@/stores/department";
import { useAccountStore } from "@/stores/account";
import { useCompanyStore } from "@/stores/company";
import { useCostStore } from "@/stores/cost";
import { useTableStore } from "@/stores/table";

import { useUtil } from "@/composables/util";
import { usePositionStore } from "@/stores/position";
import { type Employee, TRANSACT_EXAMPLE_NAME } from "@/stores/types";

const my = useUtil();

const route = useRoute();
const appStore = useAppStore();
const transactStore = useTransactStore();
const employeeStore = useEmployeeStore();
const accountStore = useAccountStore();
const companyStore = useCompanyStore();
const costStore = useCostStore();
const departmentStore = useDepartmentStore();
const positionStore = usePositionStore();
const tableStore = useTableStore();

const emit = defineEmits(["importFile"]);

const excludeZero = ref(false);

const dropFiles = ref([]);
// const csvFile = ref([{} as any])

// const importConfig = {
//   delimiter: ',', // auto-detect
//   newline: '\r\n', // auto-detect
//   quoteChar: '"',
//   escapeChar: '\\',
//   header: false,
//   transformHeader: undefined,
//   dynamicTyping: false,
//   preview: 0,
//   encoding: '',
//   worker: false,
//   comments: false,
//   step: undefined,
//   error: undefined,
//   download: false,
//   downloadRequestHeaders: undefined,
//   downloadRequestBody: undefined,
//   skipEmptyLines: true,
//   chunk: undefined,
//   chunkSize: undefined,
//   fastMode: true,
//   beforeFirstChunk: undefined,
//   withCredentials: undefined,
//   transform: undefined,
//   delimitersToGuess: [',', '\t', '|', ';', Papa.RECORD_SEP, Papa.UNIT_SEP]
// }

const importConfig = {
  delimiter: "", // auto-detect
  newline: "", // auto-detect
  quoteChar: '"',
  escapeChar: '"',
  header: false,
  transformHeader: undefined,
  dynamicTyping: false,
  preview: 0,
  encoding: "",
  worker: false,
  comments: false,
  step: undefined,
  complete: undefined,
  error: undefined,
  download: false,
  downloadRequestHeaders: undefined,
  downloadRequestBody: undefined,
  // "greedy", not true: a spreadsheet saving a blank trailing row writes bare
  // delimiters (",,,,,,"), which plain true would still hand over as a data row.
  skipEmptyLines: "greedy",
  chunk: undefined,
  chunkSize: undefined,
  fastMode: undefined,
  beforeFirstChunk: undefined,
  withCredentials: undefined,
  transform: undefined,
  delimitersToGuess: [",", "\t", "|", ";", Papa.RECORD_SEP, Papa.UNIT_SEP],
  // skipFirstNLines: 1 // skip header
};

// const exportConfig = {
//   quotes: true, //or array of booleans
//   quoteChar: '"',
//   escapeChar: '\\',
//   delimiter: ',',
//   header: true,
//   newline: '\r\n',
//   skipEmptyLines: true, //other option is 'greedy', meaning skip delimiters, quotes, and whitespace.
//   columns: null, //or array of strings
//   download: true
// }

const exportConfig = {
  quotes: false, //or array of booleans
  quoteChar: '"',
  escapeChar: '"',
  delimiter: ",",
  header: true,
  newline: "\r\n",
  skipEmptyLines: true, //other option is 'greedy', meaning skip delimiters, quotes, and whitespace.
  columns: null, //or array of strings
};

const clickDeleteFile = (index: number) => {
  dropFiles.value.splice(index, 1);
};

const clickImportFile = async (file: File) => {

  // override config if dailytime
  if (route.name == "dailytime") {
    importConfig.delimiter = "";
    importConfig.newline = "";
    importConfig.header = false;
  }

  appStore.isLoading = true;
  await Papa.parse(file, {
    ...importConfig,
    complete: (response: any) => {
      // first row is header
      let header = response.data[0];
      let result = [] as any;

      // file has header use it
      if (importConfig.header == true) {
        result = response.data
          .map((item: any) => {
            let res = {} as any;
            header.forEach((key: string, ndx: number) => {
              res[key] = item[ndx];
            });
            return res;
          })
          .splice(1);

      } else if (route.name == "transact") {
        // earnings/deductions import — CSV carries its own header row (column
        // names like account_number,id,amount). Map by header name so column
        // order/extra columns don't matter and exported files round-trip.
        const cols = (response.data[0] || []).map((c: any) => String(c ?? "").trim());
        result = response.data.slice(1).map((item: any) => {
          let res = {} as any;
          cols.forEach((key: string, i: number) => {
            res[key] = (item[i] ?? "").trim();
          });
          return res;
        });

      } else {
        // time log header
        if (route.name == "dailytime") {
          header = ["Id", "DateTime", "In", "Out", "OT_In", "OT_Out"];
        }

        // build the data
        result = response.data.map((item: any) => {
          let res = {} as any;
          item.forEach((col: any, i: number) => {
            res[header[i]] = (col ?? "").trim();
          });

          return res;
        });

      }
      emit("importFile", { type: route.name, data: result, filename: file.name });
    },
  }).finally(() => {
    setTimeout(() => {
      appStore.isLoading = false;
    }, 5 * 1000);
  });
};

// An account with nothing in it unparses to an empty file — no header row, no
// hint at what to type. Export one placeholder instead. The columns that are the
// same on every row carry their real values, so all the user fills in is the
// employee and the amount. id 0 matches no employee, so a template left as-is is
// skipped by the import rather than booked.
const sampleTransactRow = () => ({
  uuid: "",
  account_number: accountStore.accountNumber(appStore.thisAccount.uuid),
  id: 0,
  employee_name: TRANSACT_EXAMPLE_NAME,
  trasact_date: appStore.thisPeriod.start_date,
  account_side: appStore.thisAccount.account_side,
  amount: 0,
});

// MTD, YTD and Total as on the Earnings / Deductions list, as numbers Excel can add up
const toDateColumns = (employee_uuid: string) => {
  const figures = transactStore.accountToDate[employee_uuid];
  const money = (value: number | undefined) => Number((value ?? 0).toFixed(2));
  return { mtd: money(figures?.mtd), ytd: money(figures?.ytd), total: money(figures?.total) };
};

const clickExportFile = () => {

  let csvFile: string = '';
  let filename = "";

  if (route.name == "transact") {
    const rows = (transactStore.transactionsByAccount as any[]).map(
      ({ modified_on, modified_by, created_on, created_by, ip, ...item }) =>
        Object.assign({
          uuid: item.uuid,
          account_number: accountStore.accountNumber(item.account_uuid),
          id: employeeStore.id(item.employee_uuid),
          employee_name: employeeStore.fullName(item.employee_uuid),
          trasact_date: item.transact_date,
          account_side: item.account_side,
          // amount is This Period. MTD, YTD and Total are the screen's figures as of the selected
          // period, for reading only: the import ignores every column but account_number, id,
          // amount and uuid, so an exported file still re-imports.
          amount: item.amount,
          ...toDateColumns(item.employee_uuid),
        })
    );

    csvFile = Papa.unparse(rows.length ? rows : [sampleTransactRow()], exportConfig);

    filename =
      route.fullPath.toString().split("/")[2] +
        "_" +
        appStore.thisAccount.account_name.toLocaleLowerCase() +
        "_" +
        accountStore.accountNumber(appStore.thisAccount.uuid) +
        "_" +
        my.formatDate(appStore.thisPeriod.start_date).replace("/", "") +
        "_" +
        appStore.thisPeriod.uuid.split("-")[0] +
        "_" +
        appStore.thisAccount.uuid?.split("-")[0] || "export";
  } else if (route.name == "company") {
    csvFile = Papa.unparse(
      (companyStore.companies as any[]).map(
        ({ modified_on, modified_by, created_on, created_by, ip, ...item }) =>
          Object.assign({
            ...item,
          })
      ),
      exportConfig
    );
    filename = route.fullPath.toString().split("/").join("_");
  } else if (route.name == "employee") {
    csvFile = Papa.unparse(
      (employeeStore.employees as any[])
        .map(
          ({
            schedule_uuid,
            days_off,
            modified_on,
            modified_by,
            created_on,
            created_by,
            ip,
            ...item
          }) =>
            Object.assign({
              ...item,
              company: companyStore.companyName(item.company_uuid),
              department: departmentStore.departmentName(item.department_uuid),
              position: positionStore.positionName(item.position_uuid),
              cost: costStore.costCenterName(item.cost_uuid),
            })
        )
        .map(
          ({ department_uuid, position_uuid, company_uuid, cost_uuid, ...item }) => item
        ),
      exportConfig
    );

    filename = route.fullPath.toString().split("/").join("_");
  } else if (route.name == "cost") {
    csvFile = Papa.unparse(
      (costStore.costCenters as any[])
        .map(({ modified_on, modified_by, created_on, created_by, ip, ...item }) =>
          Object.assign({
            ...item,
            company: companyStore.companyName(item.company_uuid),
          })
        )
        .map(({ company_uuid, ...item }) => item),
      exportConfig
    );

    filename = route.name.toString().split("/").join("_");
  } else if (route.name == "table") {
    if (route.params.type == "ss") {
      csvFile = Papa.unparse(
        (tableStore.SS as any[]).map(
          ({ modified_on, modified_by, created_on, created_by, ip, ...item }) =>
            Object.assign({
              ...item,
            })
        ),
        exportConfig
      );
    } else if (route.params.type == "bir") {
      csvFile = Papa.unparse(
        (tableStore.BIR as any[]).map(
          ({ modified_on, modified_by, created_on, created_by, ip, ...item }) =>
            Object.assign({
              ...item,
            })
        ),
        exportConfig
      );
    } else if (route.params.type == "pf") {
      csvFile = Papa.unparse(
        (tableStore.PF as any[]).map(
          ({ modified_on, modified_by, created_on, created_by, ip, ...item }) =>
            Object.assign({
              ...item,
            })
        ),
        exportConfig
      );
    } else if (route.params.type == "ph") {
      csvFile = Papa.unparse(
        (tableStore.PH as any[]).map(
          ({ modified_on, modified_by, created_on, created_by, ip, ...item }) =>
            Object.assign({
              ...item,
            })
        ),
        exportConfig
      );
    } else if (route.params.type == "holiday") {
      csvFile = Papa.unparse(
        (tableStore.HOLIDAY as any[]).map(
          ({ modified_on, modified_by, created_on, created_by, ip, ...item }) =>
            Object.assign({
              ...item,
            })
        ),
        exportConfig
      );
    }

    filename = route.name.toString().split("/").join("_") +'_' + route.params.type.toString();
  }

  // BLOB AND FILENAME IS READY FOR EXPORT
  const blob = new Blob([csvFile], { type: "text/csv;charset=utf-8;" });

  const link = document.createElement("a");
  if (link.download !== undefined) {
    // Browsers that support HTML5 download attribute
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    // every export is CSV; without the extension Excel will not open it by double-click
    link.setAttribute("download", filename.toLowerCase().endsWith(".csv") ? filename : filename + ".csv");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
};

// uploading changes data: pay-period rows (Earnings / Deductions, daily time) or master data
const uploadCapability = computed(() =>
  route.name == "transact" || route.name == "dailytime" ? "period" : "master"
);

const showUploadFile = computed(() => {
  if (route.name == "transact") {
    return (
      appStore.uploadFile.includes(route.name as string) &&
      appStore.thisPeriod.uuid &&
      !(appStore.uploadFilter as any)[route.params.type as string].includes(
        appStore.thisAccount.account_number
      )
    );
  } else if (route.name == "dailytime") {
    return (
      appStore.uploadFile.includes(route.name as string) &&
      appStore.thisPeriod.uuid &&
      appStore.thisCompany.uuid
    );
  } else if (route.name == "employee") {
    return appStore.uploadFile.includes(route.name as string);
  } else if (route.name == "cost") {
    return appStore.uploadFile.includes(route.name as string);
  } else if (route.name == "company") {
    return appStore.uploadFile.includes(route.name as string);
  } else {
    return appStore.uploadFile.includes(route.name as string);
  }
});

const showExportFile = computed(() => {
  if (route.name == "transact") {
    return (
      appStore.uploadFile.includes(route.name as string) &&
      appStore.thisPeriod.uuid &&
      appStore.thisAccount.account_number
    );
  } else if (route.name == "employee") {
    return appStore.uploadFile.includes(route.name as string);
  } else if (route.name == "cost") {
    return appStore.uploadFile.includes(route.name as string);
  } else {
    return appStore.uploadFile.includes(route.name as string);
  }
});

const isFileUploaded = computed(() => (filename: any) => {
  const p = appStore.thisPeriod as any;
  return p.timelogs != undefined &&
    p.timelogs.split(",").includes(filename)
    ? true
    : false;
});
</script>

<style scoped></style>
